<template>
  <article class="invoice-create-card invoice-create-card--accent-contacts payment-receipt-payment-info-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-credit-card invoice-create-section-icon invoice-create-section-icon--contacts" aria-hidden="true"></i>
        {{ text?.create?.ui?.paymentInfoCard?.title || 'Información del Pago' }}
      </h3>

      <div class="payment-receipt-payment-info-card__head-actions">
        <div class="payment-receipt-payment-info-card__folio-box">
          <strong>{{ text?.create?.ui?.paymentInfoCard?.fields?.folioPrefix || 'Folio' }}: {{ paymentFolio }}</strong>
        </div>
      </div>
    </header>

    <div class="invoice-create-info-grid payment-receipt-payment-info-card__grid">
      <label class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.amount || 'Monto a pagar' }}</span>
        <AppMoneyInput
          v-model="receivedAmountModel"
          output-mode="number"
          :decimal-places="2"
          :minimum-value="0"
          :currency="currency"
        />
      </label>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.date || 'Fecha' }}</span>
        <VueDatePicker
          :model-value="info.date"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.date || 'Selecciona fecha'"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          model-type="yyyy-MM-dd"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="generalText.common.today"
          @update:model-value="$emit('info-change', { key: 'date', value: $event || '' })"
        />
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.paymentForm || 'Forma de pago' }}</span>
        <multiselect
          v-model="paymentFormModel"
          :options="paymentFormOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          class="app-modal-select"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.paymentForm || 'Selecciona forma de pago'"
        >
          <template #noResult>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.paymentAccount || 'Cuenta receptora' }}</span>
        <multiselect
          v-model="paymentAccountModel"
          :options="paymentAccountOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.paymentAccount || 'Selecciona cuenta receptora'"
        >
          <template #noResult>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <label class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.operationNumber || 'No. operación / referencia' }}</span>
        <input
          v-model.trim="operationNumberModel"
          type="text"
          class="form-control"
          maxlength="80"
          autocomplete="off"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.operationNumber || 'Captura número de operación'"
        />
      </label>

    </div>
  </article>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: generalText } = useDomainText('general');

export default {
  name: 'ExpenseReceiptCreatePaymentInfoCard',
  components: {
    AppMoneyInput,
    VueDatePicker,
  },
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    info: {
      type: Object,
      required: true,
    },
    paymentFormOptions: {
      type: Array,
      default: () => [],
    },
    paymentAccountOptions: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: 'MXN',
    },
  },
  emits: ['info-change'],
  data() {
    return {
      generalText,
    };
  },
  computed: {
    receivedAmountModel: {
      get() {
        return Number(this.info?.receivedAmount || 0) || 0;
      },
      set(value) {
        this.$emit('info-change', { key: 'receivedAmount', value: Number(value || 0) || 0 });
      },
    },
    paymentFormModel: {
      get() {
        const selectedCode = String(this.info?.paymentForm || '').trim();
        return this.paymentFormOptions.find((option) => String(option?.value || '').trim() === selectedCode)
          || this.paymentFormOptions[0]
          || null;
      },
      set(option) {
        this.$emit('info-change', { key: 'paymentForm', value: String(option?.value || '').trim() });
      },
    },
    paymentAccountModel: {
      get() {
        const selectedId = String(this.info?.paymentAccountId || '').trim();
        return this.paymentAccountOptions.find((option) => String(option?.value || '').trim() === selectedId)
          || this.paymentAccountOptions[0]
          || null;
      },
      set(option) {
        this.$emit('info-change', { key: 'paymentAccountId', value: String(option?.value || '').trim() });
      },
    },
    operationNumberModel: {
      get() {
        return String(this.info?.operationNumber || '').trim();
      },
      set(value) {
        this.$emit('info-change', { key: 'operationNumber', value: String(value || '').trim() });
      },
    },
    paymentFolio() {
      const series = String(this.info?.series || '').trim();
      const folio = String(this.info?.folio || '').trim();
      const value = `${series}${folio}`.trim();
      return value !== '' ? value : '-';
    },
  },
};
</script>

<style scoped>
.payment-receipt-payment-info-card {
  display: grid;
  gap: 8px;
  align-content: start;
  padding-top: 10px;
}

.payment-receipt-payment-info-card .invoice-create-card__head {
  align-items: flex-start;
  margin-bottom: 6px;
}

.payment-receipt-payment-info-card__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.payment-receipt-payment-info-card__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.payment-receipt-payment-info-card__folio-box {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px 10px;
}

.payment-receipt-payment-info-card__folio-box strong {
  color: #0f172a;
  font-size: 13px;
}

@media (max-width: 1200px) {
  .payment-receipt-payment-info-card__grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .payment-receipt-payment-info-card__grid {
    grid-template-columns: 1fr;
  }
}
</style>
